import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, MoveUp, MoveDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface SignatureBlock {
  id: string;
  signature_purpose: 'member_acceptance' | 'board_approval' | 'board_witness' | 'secretary_certification' | 'officer_acceptance' | 'notary_acknowledgment';
  signature_label: string;
  description: string;
  required_role: string;
  signer_placeholder: string;
  signature_block_text: string;
  order: number;
}

interface SignatureBlockManagerProps {
  blocks: SignatureBlock[];
  onChange: (blocks: SignatureBlock[]) => void;
}

export function SignatureBlockManager({ blocks, onChange }: SignatureBlockManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<SignatureBlock | null>(null);
  const [formData, setFormData] = useState<Partial<SignatureBlock>>({
    signature_purpose: 'member_acceptance',
    signature_label: '',
    description: '',
    required_role: 'member',
    signer_placeholder: '{{member_name}}',
    signature_block_text: '',
  });

  const openDialog = (block?: SignatureBlock) => {
    if (block) {
      setFormData(block);
      setEditingBlock(block);
    } else {
      setFormData({
        signature_purpose: 'member_acceptance',
        signature_label: '',
        description: '',
        required_role: 'member',
        signer_placeholder: '{{member_name}}',
        signature_block_text: '',
      });
      setEditingBlock(null);
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.signature_label || !formData.signature_block_text) return;

    if (editingBlock) {
      onChange(
        blocks.map((b) => (b.id === editingBlock.id ? { ...editingBlock, ...formData } as SignatureBlock : b))
      );
    } else {
      const newBlock: SignatureBlock = {
        id: crypto.randomUUID(),
        signature_purpose: formData.signature_purpose || 'member_acceptance',
        signature_label: formData.signature_label || '',
        description: formData.description || '',
        required_role: formData.required_role || 'member',
        signer_placeholder: formData.signer_placeholder || '{{member_name}}',
        signature_block_text: formData.signature_block_text || '',
        order: blocks.length + 1,
      };
      onChange([...blocks, newBlock]);
    }
    setDialogOpen(false);
  };

  const handleDelete = (blockId: string) => {
    onChange(blocks.filter((b) => b.id !== blockId));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    onChange(newBlocks.map((b, i) => ({ ...b, order: i + 1 })));
  };

  const handleMoveDown = (index: number) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    onChange(newBlocks.map((b, i) => ({ ...b, order: i + 1 })));
  };

  const purposeOptions = [
    { value: 'member_acceptance', label: 'Member Acceptance' },
    { value: 'board_approval', label: 'Board Approval' },
    { value: 'board_witness', label: 'Board Witness' },
    { value: 'secretary_certification', label: 'Secretary Certification' },
    { value: 'officer_acceptance', label: 'Officer Acceptance' },
    { value: 'notary_acknowledgment', label: 'Notary Acknowledgment' },
  ];

  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'board_member_active', label: 'Board Member (Active)' },
    { value: 'treasury_secretary', label: 'Secretary Treasurer' },
    { value: 'officer', label: 'Officer' },
    { value: 'president', label: 'President' },
    { value: 'vice_president', label: 'Vice President' },
    { value: 'secretary', label: 'Secretary' },
    { value: 'treasurer', label: 'Treasurer' },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Signature Blocks</CardTitle>
        <Button size="sm" onClick={() => openDialog()}>
          <Plus className="h-3 w-3 mr-1" />
          Add Signature Block
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No signature blocks defined. Add signature blocks to specify who needs to sign this document and what they're signing for.
          </p>
        ) : (
          blocks
            .sort((a, b) => a.order - b.order)
            .map((block, index) => (
              <div
                key={block.id}
                className="flex items-start gap-2 p-3 border rounded-lg hover:bg-muted/50"
              >
                <div className="flex-1">
                  <h4 className="font-medium">{block.signature_label}</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {block.description}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                      Purpose: {block.signature_purpose.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs bg-muted px-2 py-0.5 rounded">
                      Role: {block.required_role.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    "{block.signature_block_text}"
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === blocks.length - 1}
                  >
                    <MoveDown className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openDialog(block)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(block.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))
        )}
      </CardContent>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBlock ? 'Edit Signature Block' : 'Add Signature Block'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="signature_label">Signature Label *</Label>
              <Input
                id="signature_label"
                value={formData.signature_label}
                onChange={(e) => setFormData({ ...formData, signature_label: e.target.value })}
                placeholder="e.g., Member Acceptance Signature"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The display name for this signature block
              </p>
            </div>

            <div>
              <Label htmlFor="signature_purpose">Signature Purpose *</Label>
              <Select
                value={formData.signature_purpose}
                onValueChange={(value: any) => setFormData({ ...formData, signature_purpose: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {purposeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                What this signature represents (acceptance, approval, certification, etc.)
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., New member signature accepting responsibilities"
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Brief explanation of what this signature means (shown to signers)
              </p>
            </div>

            <div>
              <Label htmlFor="required_role">Required Role *</Label>
              <Select
                value={formData.required_role}
                onValueChange={(value: any) => setFormData({ ...formData, required_role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Which UNA member role can fulfill this signature requirement
              </p>
            </div>

            <div>
              <Label htmlFor="signer_placeholder">Signer Placeholder</Label>
              <Input
                id="signer_placeholder"
                value={formData.signer_placeholder}
                onChange={(e) => setFormData({ ...formData, signer_placeholder: e.target.value })}
                placeholder="e.g., {{member_name}}"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Template variable for signer name (used in document generation)
              </p>
            </div>

            <div>
              <Label htmlFor="signature_block_text">Signature Block Text *</Label>
              <Textarea
                id="signature_block_text"
                value={formData.signature_block_text}
                onChange={(e) => setFormData({ ...formData, signature_block_text: e.target.value })}
                placeholder="e.g., I accept membership in this Unincorporated Nonprofit Association and agree to fulfill all duties and responsibilities."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The certification text that appears above the signature line on the signed PDF. This is what the signer is certifying by signing.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!formData.signature_label || !formData.signature_block_text}
            >
              {editingBlock ? 'Save Changes' : 'Add Signature Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
